import React, { useEffect, useState } from "react";
import axios from "axios";
import Modal from "react-modal";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Swal from "sweetalert2";
import { FaBell, FaHeart, FaInstagram, FaPalette, FaShoppingCart, FaSlidersH, FaStore } from "react-icons/fa";

import { AuthProvider, useAuth } from "./components/Login/AuthContext";
import Login from "./components/Login/Login";
import RegistroUsuario from "./components/Usuario/RegistroUsuario";
import Dashboard from "./components/Dashboard/DashboardPrincipal";
import ListaProductos from "./components/Tienda/ListaProductos";
import RegistroProducto from "./components/Tienda/RegistroProducto";
import ListaCategorias from "./components/Tienda/ListaCategorias";
import ListaUsuarios from "./components/Usuario/ListaUsuarios";
import ListaProductoCliente from "./components/Tienda/ListaProductoCliente";
import DetalleProducto from "./components/Tienda/DetalleProducto";
import Carrito from "./components/Tienda/Carrito";
import { CarritoProvider, useCarrito } from "./components/Tienda/ContextoCarrito";
import { FavoritosProvider, useFavoritos } from "./components/Tienda/ContextoFavoritos";
import Favoritos from "./components/Tienda/Favoritos";
import PagoExitoso from "./components/Pago/PagoExitoso";
import PagoFallido from "./components/Pago/PagoFallido";
import PagoPendiente from "./components/Pago/PagoPendiente";
import WhatsAppButton from "./components/WhatsAppButton";
import SolicitudCuadroPersonalizado from "./components/CuadrosPersonalizados/SolicitudCuadroPersonalizado";
import AdminCuadrosPersonalizados from "./components/CuadrosPersonalizados/AdminCuadrosPersonalizados";
import { ConfiguracionSitioProvider, useConfiguracionSitio } from "./components/Configuracion/ContextoConfiguracionSitio";
import EditorPersonalizacion from "./components/Configuracion/EditorPersonalizacion";

Modal.setAppElement("#root");

const LogoCasaArte = () => {
  const { logoUrl, imagenPredeterminada, personalizacion } = useConfiguracionSitio();
  const tamano = { pequeno: 36, normal: 44, grande: 56 }[personalizacion.logo_tamano];
  return (
    <span className="flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-stone-200 bg-white shadow-sm ring-2 ring-amber-100 transition duration-300 group-hover:scale-105 group-hover:shadow-md" style={{ width: tamano, height: tamano }}>
      <img
        src={logoUrl}
        alt="Logo de Casa Arte"
        className="h-full w-full object-cover object-center"
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = imagenPredeterminada;
        }}
      />
    </span>
  );
};

const RutaAdmin = ({ children }) => {
  const { usuario, cargando } = useAuth();
  if (cargando) return null;
  if (!usuario || usuario.rol !== "admin") return <Navigate to="/login" />;
  return children;
};

const RutaCliente = ({ children }) => {
  const { usuario, cargando } = useAuth();
  const location = useLocation();
  if (cargando) return null;
  if (!usuario || usuario.rol !== "cliente") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
};

const RutaAutenticada = ({ children }) => {
  const { usuario, cargando } = useAuth();
  const location = useLocation();
  if (cargando) return null;
  if (!usuario) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
};

const NavPublica = () => {
  const { personalizacion } = useConfiguracionSitio();
  return (
  <nav className="site-nav bg-white text-gray-900 px-4 sm:px-8 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-3 sm:gap-8 border-b border-gray-200 sticky top-0 z-40">
    <Link to="/" className="group flex min-w-0 items-center gap-3 font-bold text-lg">
      <LogoCasaArte />
      <span className="hidden sm:inline truncate">{personalizacion.nombre_tienda}</span>
    </Link>

    <div className="flex gap-8 items-center justify-center flex-1 hidden md:flex">
      <Link to="/tienda" className="text-gray-700 hover:text-stone-800 font-medium text-sm transition">
        TIENDA
      </Link>
      <Link to="/personaliza-tu-cuadro" className="text-gray-700 hover:text-stone-800 font-medium text-sm transition">
        PERSONALIZA TU CUADRO
      </Link>
    </div>

    <div className="flex gap-2 sm:gap-4 items-center">
      <Link
        to="/carrito"
        className="relative p-2 text-gray-700 hover:text-stone-800 transition text-lg"
        title="Carrito"
        aria-label="Carrito"
      >
        <FaShoppingCart />
      </Link>
      <Link
        to="/login"
        className="whitespace-nowrap px-2 py-2 text-xs font-medium text-gray-700 transition hover:text-stone-800 sm:px-4 sm:text-sm"
      >
        LOG IN
      </Link>
    </div>

    <div className="order-last flex w-full gap-4 overflow-x-auto border-t border-gray-100 pt-3 text-xs font-semibold uppercase tracking-wide md:hidden">
      <Link to="/tienda" className="whitespace-nowrap text-gray-700 hover:text-stone-800">
        Tienda
      </Link>
      <Link to="/personaliza-tu-cuadro" className="whitespace-nowrap text-gray-700 hover:text-stone-800">
        Personaliza tu cuadro
      </Link>
    </div>
  </nav>
  );
};

const NavAdmin = () => {
  const { logout, token } = useAuth();
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState(0);

  useEffect(() => {
    let activo = true;
    const cargarNotificaciones = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/cuadros-personalizados/resumen`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (activo) {
          setNotificaciones(Number(response.data?.no_leidas ?? response.data?.nuevas ?? 0));
        }
      } catch (error) {
        if (activo) setNotificaciones(0);
      }
    };

    cargarNotificaciones();
    const intervalId = window.setInterval(cargarNotificaciones, 60000);
    window.addEventListener("cuadros-personalizados-actualizados", cargarNotificaciones);
    return () => {
      activo = false;
      window.clearInterval(intervalId);
      window.removeEventListener("cuadros-personalizados-actualizados", cargarNotificaciones);
    };
  }, [token]);

  const handleLogout = () => {
    logout();
    Swal.fire({ icon: "info", title: "Sesión cerrada", showConfirmButton: false, timer: 1200 });
    navigate("/login");
  };

  return (
    <nav className="site-nav bg-white text-gray-900 px-4 sm:px-8 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-3 sm:gap-8 border-b border-gray-200 sticky top-0 z-40">
      <Link to="/dashboard" className="group flex min-w-0 items-center gap-3 font-bold text-lg">
        <LogoCasaArte />
        <span className="hidden sm:inline text-gray-800 truncate">Admin Panel</span>
      </Link>

      <div className="flex gap-6 items-center justify-center flex-1 hidden lg:flex">
        <Link to="/dashboard" className="text-gray-700 hover:text-stone-800 font-medium text-sm transition">
          Dashboard
        </Link>
        <Link to="/admin/productos" className="text-gray-700 hover:text-stone-800 font-medium text-sm transition">
          Productos
        </Link>
        <Link to="/admin/categorias" className="text-gray-700 hover:text-stone-800 font-medium text-sm transition">
          Categorias
        </Link>
        <Link to="/admin/usuarios" className="text-gray-700 hover:text-stone-800 font-medium text-sm transition">
          Usuarios
        </Link>
        <Link to="/admin/cuadros-personalizados" className="relative inline-flex items-center gap-2 text-gray-700 hover:text-stone-800 font-medium text-sm transition">
          <FaBell /> Personalizados
          {notificaciones > 0 && <Badge value={notificaciones} />}
        </Link>
        <Link
          to="/admin/personalizacion"
          className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-sm font-semibold text-stone-800 transition hover:border-amber-400 hover:bg-amber-50"
          title="Personalizar el diseño de la tienda y del administrador"
        >
          <FaSlidersH /> Diseño
        </Link>
        <Link
          to="/tienda"
          className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-900 transition hover:border-amber-300 hover:bg-amber-100"
          title="Ver la tienda como se muestra al público"
        >
          <FaStore /> Ver tienda
        </Link>
        
      </div>

      <div className="flex gap-2 sm:gap-4 items-center">
        <span className="text-sm text-gray-600">Admin</span>
        <button
          onClick={handleLogout}
          className="whitespace-nowrap rounded-lg bg-black px-3 py-2 text-xs font-medium text-white transition hover:bg-gray-900 sm:px-4 sm:text-sm"
        >
          Cerrar sesión
        </button>
      </div>

      <div className="order-last -mx-4 flex w-screen items-center gap-2 overflow-x-auto border-t border-stone-200 bg-stone-50 px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide sm:-mx-8 sm:px-8 lg:hidden">
        <Link
          to="/tienda"
          className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-amber-300 bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-2 font-extrabold text-amber-950 shadow-sm transition hover:border-amber-400 hover:shadow-md"
          title="Ver la tienda como se muestra al público"
        >
          <FaStore /> Ver tienda
        </Link>
        <Link to="/admin/personalizacion" className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-stone-300 bg-white px-4 py-2 font-bold text-stone-800 transition hover:border-amber-400 hover:bg-amber-50">
          <FaSlidersH /> Diseño
        </Link>
        <Link to="/dashboard" className="shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-gray-700 transition hover:bg-white hover:text-stone-900">
          Dashboard
        </Link>
        <Link to="/admin/productos" className="shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-gray-700 transition hover:bg-white hover:text-stone-900">
          Productos
        </Link>
        <Link to="/admin/categorias" className="shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-gray-700 transition hover:bg-white hover:text-stone-900">
          Categorias
        </Link>
        <Link to="/admin/usuarios" className="shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-gray-700 transition hover:bg-white hover:text-stone-900">
          Usuarios
        </Link>
        <Link to="/admin/cuadros-personalizados" className="relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-gray-700 transition hover:bg-white hover:text-stone-900">
          Personalizados
          {notificaciones > 0 && <Badge value={notificaciones} />}
        </Link>
      </div>
    </nav>
  );
};

const NavCliente = () => {
  const { usuario, logout } = useAuth();
  const { personalizacion } = useConfiguracionSitio();
  const { cantidadTotal } = useCarrito();
  const { cantidadFavoritos } = useFavoritos();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    Swal.fire({ icon: "info", title: "Sesión cerrada", showConfirmButton: false, timer: 1200 });
    navigate("/login");
  };

  return (
    <nav className="site-nav bg-white text-gray-900 px-4 sm:px-8 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-3 sm:gap-8 border-b border-gray-200 sticky top-0 z-40">
      <Link to="/tienda" className="group flex min-w-0 items-center gap-3 font-bold text-lg">
        <LogoCasaArte />
        <span className="hidden sm:inline truncate">{personalizacion.nombre_tienda}</span>
      </Link>

      <div className="flex gap-8 items-center justify-center flex-1 hidden md:flex">
        <Link to="/tienda" className="text-gray-700 hover:text-stone-800 font-medium text-sm transition">
          TIENDA
        </Link>
        <Link to="/personaliza-tu-cuadro" className="inline-flex items-center gap-2 text-gray-700 hover:text-stone-800 font-medium text-sm transition">
          <FaPalette /> PERSONALIZA TU CUADRO
        </Link>
      </div>

      <div className="flex gap-2 sm:gap-4 items-center">
      <Link
        to="/favoritos"
        className="relative p-2 text-gray-700 transition hover:text-rose-600"
        title="Mis favoritos"
        aria-label={`Mis favoritos: ${cantidadFavoritos}`}
      >
        <FaHeart />
        {cantidadFavoritos > 0 && <Badge value={cantidadFavoritos} />}
      </Link>
      <Link
        to="/carrito"
        className="relative p-2 text-gray-700 hover:text-stone-800 transition text-lg"
        title="Carrito"
        aria-label={`Carrito: ${cantidadTotal} productos`}
      >
        <FaShoppingCart />
        {cantidadTotal > 0 && <Badge value={cantidadTotal} />}
      </Link>
        <div className="w-px h-6 bg-gray-300"></div>
        <span className="text-gray-600 text-sm hidden sm:inline">
          Hola, {usuario?.nombre || usuario?.username}
        </span>
        <button
          onClick={handleLogout}
          className="whitespace-nowrap px-2 py-2 text-xs font-medium text-gray-700 transition hover:text-stone-800 sm:px-4 sm:text-sm"
        >
          Cerrar sesión
        </button>
      </div>

      <div className="order-last flex w-full gap-4 overflow-x-auto border-t border-gray-100 pt-3 text-xs font-semibold uppercase tracking-wide md:hidden">
        <Link to="/tienda" className="whitespace-nowrap text-gray-700 hover:text-stone-800">
          Tienda
        </Link>
        <Link to="/carrito" className="whitespace-nowrap text-gray-700 hover:text-stone-800">
          Carrito {cantidadTotal > 0 && `(${cantidadTotal})`}
        </Link>
        <Link to="/favoritos" className="whitespace-nowrap text-gray-700 hover:text-stone-800">
          Favoritos {cantidadFavoritos > 0 && `(${cantidadFavoritos})`}
        </Link>
        <Link to="/personaliza-tu-cuadro" className="whitespace-nowrap text-gray-700 hover:text-stone-800">
          Personaliza tu cuadro
        </Link>
      </div>
    </nav>
  );
};

const Footer = () => {
  const { personalizacion } = useConfiguracionSitio();
  return (
  <footer className="site-footer border-t border-stone-200 bg-stone-950 text-stone-200">
    <div className="mx-auto grid w-full max-w-7xl gap-8 px-6 py-10 md:grid-cols-[1.4fr_0.8fr_1fr] md:px-8">
      <div className="max-w-md">
        <Link to="/tienda" className="group inline-flex items-center gap-3">
          <LogoCasaArte />
          <span className="text-xl font-bold text-white">{personalizacion.nombre_tienda}</span>
        </Link>
        <p className="mt-4 text-sm leading-relaxed text-stone-400">
          {personalizacion.descripcion_footer}
        </p>
      </div>

      <div>
        <h2 className="text-sm font-bold uppercase tracking-widest text-white">
          Explora
        </h2>
        <div className="mt-4 flex flex-col items-start gap-3 text-sm text-stone-400">
          <Link to="/tienda" className="transition hover:text-amber-300">
            Tienda
          </Link>
          <Link
            to="/personaliza-tu-cuadro"
            className="transition hover:text-amber-300"
          >
            Personaliza tu cuadro
          </Link>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-bold uppercase tracking-widest text-white">
          Síguenos
        </h2>
        <a
          href={personalizacion.instagram_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-3 rounded-full border border-stone-700 px-4 py-2.5 text-sm font-semibold text-stone-200 transition hover:border-pink-400 hover:bg-pink-500/10 hover:text-pink-300"
          aria-label="Visitar Instagram de Casa Arte"
        >
          <FaInstagram className="text-xl" />
          @casa_arte.lg
        </a>
      </div>
    </div>

    <div className="border-t border-stone-800 px-6 py-4 text-center text-xs text-stone-500">
      © {new Date().getFullYear()} {personalizacion.nombre_tienda}. Todos los derechos reservados.
    </div>
  </footer>
  );
};

const AppContent = () => {
  const { usuario, cargando } = useAuth();
  const { personalizacion } = useConfiguracionSitio();

  if (cargando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-600 text-lg font-semibold">Cargando...</p>
      </div>
    );
  }

  const esAdmin = usuario?.rol === "admin";
  const fuente = {
    moderna: "Inter, system-ui, sans-serif",
    clasica: "Georgia, 'Times New Roman', serif",
    elegante: "'Trebuchet MS', system-ui, sans-serif",
  }[personalizacion.fuente];
  const radio = { rectos: "0px", suaves: "10px", redondos: "22px" }[personalizacion.bordes];
  const prefijo = esAdmin ? "admin" : "tienda";

  return (
    <div className="site-theme flex flex-col min-h-screen" style={{
      "--tema-principal": personalizacion[`${prefijo}_principal`],
      "--tema-acento": personalizacion[`${prefijo}_acento`],
      "--tema-fondo": personalizacion[`${prefijo}_fondo`],
      "--tema-texto": personalizacion[`${prefijo}_texto`],
      "--tema-cabecera": personalizacion[`${prefijo}_cabecera`],
      "--tema-footer": personalizacion.tienda_footer,
      "--tema-tarjeta": personalizacion[`${prefijo}_tarjeta`],
      "--tema-borde": personalizacion[`${prefijo}_borde`],
      "--tema-radio": radio,
      "--tema-fuente": fuente,
      "--tema-escala": { compacto: 0.9, normal: 1, grande: 1.1 }[personalizacion.tamano_texto],
    }}>
      {!usuario && <NavPublica />}
      {usuario?.rol === "admin" && <NavAdmin />}
      {usuario?.rol === "cliente" && <NavCliente />}

      <div className="site-content flex-1 bg-white">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/registro" element={<RegistroUsuario />} />

            <Route path="/tienda" element={<ListaProductoCliente />} />
            <Route path="/producto/:id" element={<DetalleProducto />} />

            <Route
              path="/"
              element={
                usuario?.rol === "admin" ? <Navigate to="/dashboard" /> :
                  usuario?.rol === "cliente" ? <Navigate to="/tienda" /> :
                    <Navigate to="/tienda" />
              }
            />

            <Route path="/dashboard" element={<RutaAdmin><Dashboard /></RutaAdmin>} />
            <Route path="/admin/productos" element={<RutaAdmin><ListaProductos /></RutaAdmin>} />
            <Route path="/admin/productos/registro" element={<RutaAdmin><RegistroProducto /></RutaAdmin>} />
            <Route path="/admin/categorias" element={<RutaAdmin><ListaCategorias /></RutaAdmin>} />
            <Route path="/admin/usuarios" element={<RutaAdmin><ListaUsuarios /></RutaAdmin>} />
            <Route path="/admin/cuadros-personalizados" element={<RutaAdmin><AdminCuadrosPersonalizados /></RutaAdmin>} />
            <Route path="/admin/personalizacion" element={<RutaAdmin><EditorPersonalizacion /></RutaAdmin>} />

            <Route path="/carrito" element={<RutaCliente><Carrito /></RutaCliente>} />
            <Route path="/favoritos" element={<RutaCliente><Favoritos /></RutaCliente>} />
            <Route path="/personaliza-tu-cuadro" element={<RutaAutenticada><SolicitudCuadroPersonalizado /></RutaAutenticada>} />

            <Route path="/pago/exitoso" element={<PagoExitoso />} />
            <Route path="/pago/fallido" element={<PagoFallido />} />
            <Route path="/pago/pendiente" element={<PagoPendiente />} />
            <Route path="*" element={<Navigate to="/tienda" />} />
          </Routes>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

const Badge = ({ value }) => (
  <span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-extrabold leading-4 text-white shadow-sm">
    {value > 99 ? "99+" : value}
  </span>
);

const App = () => (
  <Router>
    <AuthProvider>
      <ConfiguracionSitioProvider>
        <CarritoProvider>
          <FavoritosProvider>
            <AppContent />
          </FavoritosProvider>
        </CarritoProvider>
      </ConfiguracionSitioProvider>
    </AuthProvider>
  </Router>
);

export default App;
